import { env_url } from './endpoints'
import { getPlayerImageURL, getPlayerLink, formatNumberWithDecimal } from './shared_helpers.js'
import { handleError } from 'utils/js/error_helpers'

export const NBAsilhouette = `${env_url}/player-silhouette-hockey-cut.png`

export const imgErrNBA = ev => {
    ev.target.src = NBAsilhouette
}

export const imgErrNBASVG = ev => {
    try {
        ev.target.href.baseVal = NBAsilhouette
    } catch (e) {
        handleError(e)
    }
}

/*
 * formatQuarterName: This function returns the text label of a quarter based on it's numerical value
 *
 * @param {int} quarter - integer corresponding to a quarter
 * @return {string} quarter - text representing the period in plain english
 */
export const formatQuarterName = quarter => {
    switch (quarter) {
        case 1:
            return '1st'
        case 2:
            return 'Half'
        case 3:
            return '3rd'
        case 4:
            return '4th'
        case 5:
            return 'OT'
        default:
            return quarter - 4 + 'OT'
    }
}

export const labelQuarters = quarters => {
    let quartersCopy = quarters.slice(0)

    quartersCopy.forEach((quarter, i) => {
        if (i < 4) {
            quarter.label = i + 1
        } else if (i === 4) {
            quarter.label = 'OT'
        } else {
            quarter.label = i - 3 + 'OT'
        }
    })

    return quartersCopy
}

export const nbaPriorityStat = (blocked_shots, three_pointers_made, steals) => {
    return blocked_shots > three_pointers_made
        ? blocked_shots > steals
            ? { key: 'BLK', val: blocked_shots }
            : { key: 'STL', val: steals }
        : three_pointers_made > steals
        ? { key: '3PT', val: three_pointers_made }
        : { key: 'STL', val: steals }
}

export const nbaTeamNameIDArrayMap = new Map([
    [1, 'atlanta-hawks'],
    [2, 'boston-celtics'],
    [3, 'new-orleans-pelicans'],
    [4, 'chicago-bulls'],
    [5, 'cleveland-cavaliers'],
    [6, 'dallas-mavericks'],
    [7, 'denver-nuggets'],
    [8, 'detroit-pistons'],
    [9, 'golden-state-warriors'],
    [10, 'houston-rockets'],
    [11, 'indiana-pacers'],
    [12, 'los-angeles-clippers'],
    [13, 'los-angeles-lakers'],
    [14, 'miami-heat'],
    [15, 'milwaukee-bucks'],
    [16, 'minnesota-timberwolves'],
    [17, 'brooklyn-nets'],
    [18, 'new-york-knicks'],
    [19, 'orlando-magic'],
    [20, 'philadelphia-76ers'],
    [21, 'phoenix-suns'],
    [22, 'portland-trail-blazers'],
    [23, 'sacramento-kings'],
    [24, 'san-antonio-spurs'],
    [25, 'oklahoma-city-thunder'],
    [26, 'utah-jazz'],
    [27, 'washington-wizards'],
    [28, 'toronto-raptors'],
    [29, 'memphis-grizzlies'],
    [5312, 'charlotte-hornets'],
])

export function formatLastNineEvents(lastNineEvents = [], homeTeam, visitingTeam) {
    const massagedPlays = lastNineEvents.map(play => {
        const formattedPlayDescription = play.description
        const teamLogo =
            play.team_id === homeTeam.id ? homeTeam.image_url_90 : visitingTeam.image_url_90

        return {
            id: play.id,
            title: setPlayTitle(play),
            color: getPlayColor(play.event_id),
            bodyText: shortenPlayerNames(formattedPlayDescription, play.players),
            teamLogo: teamLogo,
            showPlayerHeadshot: play.event_id === 11 ? false : true,
            playerHeadshot: setPlayerHeadshot(play.player_id),
            playerHeadshotError: imgErrNBA,
        }
    })

    const ghostPlays = [
        { id: 1, isGhost: true },
        { id: 2, isGhost: true },
        { id: 3, isGhost: true },
        { id: 4, isGhost: true },
        { id: 5, isGhost: true },
        { id: 6, isGhost: true },
        { id: 7, isGhost: true },
        { id: 8, isGhost: true },
        { id: 9, isGhost: true },
    ]

    return massagedPlays.concat(ghostPlays.slice(massagedPlays.length))
}

function setPlayTitle(play) {
    /* insert logic to rename events if needed */
    let formattedEvent = play
    let formattedQuarter = formatQuarterName(play.quarter)

    switch (play.event_id) {
        case 1:
        case 2:
            formattedEvent = 'FT'
            break
        case 3:
        case 4:
            formattedEvent = 'FG'
            break
        case 5:
            formattedEvent = 'OREB'
            break
        case 6:
            formattedEvent = 'DREB'
            break
        case 7:
            formattedEvent = 'TO'
            break
        case 8:
            formattedEvent = 'FOUL'
            break
        case 9:
            formattedEvent = 'VIOL'
            break
        case 10:
            formattedEvent = 'SUB'
            break
        case 11:
            formattedEvent = 'TIMEOUT'
            break
        case 12:
            formattedEvent = 'JUMP'
            break
        case 13:
            formattedEvent = 'EJECT'
            break
        case 14:
        case 15:
            formattedEvent = 'REPLAY'
            break
        default:
            formattedEvent = play.event
    }

    formattedEvent = `${formattedEvent} - ${play.time} / ${formattedQuarter}`

    return formattedEvent || ''
}

function shortenPlayerNames(originalText, players = []) {
    let newText = originalText

    if (!players.length) {
        return originalText
    }

    for (const player of players) {
        const textToReplace = `${player.first_name} ${player.last_name}`
        const playerInitial = player.first_name.charAt(0)
        const replacementText = `<b>${playerInitial}. ${player.last_name}</b>`

        newText = newText.replace(textToReplace, replacementText)
    }
    return newText
}

function getPlayColor(playTypeId) {
    const darkBlue = '#0B223E'
    const red = '#DA291C'
    const grey = '#636A71'
    const brightBlue = '#0D5EB8'

    const playColorMap = new Map([
        [1, darkBlue],
        [2, darkBlue],
        [3, darkBlue],
        [4, darkBlue],
        [5, brightBlue],
        [6, brightBlue],
        [7, brightBlue],
        [8, red],
        [9, red],
        [10, red],
        [11, red],
        [12, grey],
        [13, red],
        [14, brightBlue],
        [15, grey],
    ])

    return playColorMap.get(playTypeId) || grey
}

function setPlayerHeadshot(playerID) {
    return getPlayerImageURL(playerID, 'xs')
}

export function formatInjuries(teamInjuries = []) {
    const formattedInjuries = teamInjuries.map(injury => {
        return {
            playerName: getPlayerLink(injury),
            playerPosition: injury.short_position,
            status: `${injury.status.charAt(0).toUpperCase()}${injury.status.slice(1)}`,
            disabledListStatus: injury.disability_list_status,
            timestamp: injury.timestamp,
            emptyColumn: ' ',
        }
    })

    return formattedInjuries.sort((a, b) => b.timestamp - a.timestamp)
}

export function formatSeasonLeaders(homeTeam, visitingTeam) {
    const leaderCategories = [
        {
            title: 'Points',
            categoryKey: 'points_per_game_leader',
            valueKey: 'total',
        },
        {
            title: 'Assists',
            categoryKey: 'assists_per_game_leader',
            valueKey: 'total',
        },
        {
            title: 'Rebounds',
            categoryKey: 'rebounds_per_game_leader',
            valueKey: 'total',
        },
        {
            title: 'FG%',
            categoryKey: 'field_goal_percentage_leader',
            valueKey: 'total',
            isPercentage: true,
        },
    ]

    const seasonLeaders = leaderCategories.map(leaderCategory => {
        const homeTeamData = homeTeam?.season_leaders?.[leaderCategory.categoryKey]
        const visitingTeamData = visitingTeam?.season_leaders?.[leaderCategory.categoryKey]

        if (!homeTeamData || !visitingTeamData) {
            return null
        }

        let homeTeamValue = homeTeamData[leaderCategory.valueKey]
        let visitingTeamValue = visitingTeamData[leaderCategory.valueKey]

        if (leaderCategory.isPercentage) {
            homeTeamValue = (homeTeamValue * 100).toFixed(1)
            visitingTeamValue = (visitingTeamValue * 100).toFixed(1)
        }

        return {
            title: leaderCategory.title,
            homeTeamShortName: homeTeam.short_name,
            homeTeamColor: homeTeam.color,
            visitingTeamColor: visitingTeam.color,
            visitingTeamShortName: visitingTeam.short_name,
            homeTeamPlayerImage: homeTeamData.image_urls.xs,
            visitingTeamPlayerImage: visitingTeamData.image_urls.xs,
            homeTeamLogo: homeTeam.image_url_90,
            visitingTeamLogo: visitingTeam.image_url_90,
            homeTeamValue: homeTeamValue,
            visitingTeamValue: visitingTeamValue,
            homeTeamPlayerName: `${homeTeamData.first_name.charAt(0)}. ${homeTeamData.last_name}`,
            visitingTeamPlayerName: `${visitingTeamData.first_name.charAt(0)}. ${
                visitingTeamData.last_name
            }`,
        }
    })
    return seasonLeaders.filter(n => n) // filter out null values
}

export function formatTeamStats(homeTeam, visitingTeam) {
    if (!visitingTeam.season_stats || !homeTeam.season_stats) {
        return []
    }

    const statCategories = [
        {
            title: 'Points Per Game',
            valueKey: 'points_per_game',
            valueFormatter: n => formatNumberWithDecimal(n, 1),
        },
        {
            title: 'Points Allowed Per Game',
            valueFormatter: n => formatNumberWithDecimal(n, 1),
            valueKey: 'points_allowed_per_game',
        },
        {
            title: 'FG%',
            valueFormatter: n => formatNumberWithDecimal(n * 100, 1),
            valueKey: 'field_goal_percent',
            isPercent: true,
        },
        {
            title: '3PT%',
            valueFormatter: n => formatNumberWithDecimal(n * 100, 1),
            valueKey: 'three_point_shooting_percent',
            isPercent: true,
        },
        {
            title: 'FT%',
            valueFormatter: n => formatNumberWithDecimal(n * 100, 1),
            valueKey: 'free_throw_pct',
            isPercent: true,
        },
        {
            title: 'Turnovers Per Game',
            valueFormatter: n => formatNumberWithDecimal(n, 1),
            valueKey: 'turnovers_per_game',
        },
        {
            title: 'Rebounds Per Game',
            valueFormatter: n => formatNumberWithDecimal(n, 1),
            valueKey: 'rebounds_per_game',
        },
        {
            title: 'Assists Per Game',
            valueFormatter: n => formatNumberWithDecimal(n, 1),
            valueKey: 'assists_per_game',
        },
    ]

    const teamStats = statCategories.map(statCategory => {
        let homeTeamValue = homeTeam.season_stats[statCategory.valueKey]
        let visitingTeamValue = visitingTeam.season_stats[statCategory.valueKey]

        if (
            (!homeTeamValue && homeTeamValue !== 0) ||
            (!visitingTeamValue && visitingTeamValue !== 0)
        ) {
            return null
        }

        return {
            title: statCategory.title,
            homeTeamValue: homeTeamValue,
            visitingTeamValue: visitingTeamValue,
            isPercent: statCategory.isPercent,
            valueFormatter: statCategory.valueFormatter,
        }
    })
    return teamStats.filter(n => n) // filter out null values
}

export function formatGameStats(homeTeam, visitingTeam) {
    if (!visitingTeam.game_stats || !homeTeam.game_stats) {
        return []
    }

    const statCategories = [
        {
            title: 'FG%',
            valueKey: 'field_goals_pct',
            valueFormatter: n => `${formatNumberWithDecimal(n, 1)}`,
            isPercent: true,
        },
        {
            title: '3PT%',
            valueKey: 'three_point_pct',
            valueFormatter: n => `${formatNumberWithDecimal(n, 1)}`,
            isPercent: true,
        },
        {
            title: 'FT%',
            valueKey: 'free_throws_pct',
            valueFormatter: n => `${formatNumberWithDecimal(n, 1)}`,
            isPercent: true,
        },
        {
            title: 'Turnovers',
            valueKey: 'turnovers',
        },
        {
            title: 'Rebounds',
            valueKey: 'rebounds_total',
        },
        {
            title: 'Assists',
            valueKey: 'assists',
        },
    ]

    const gameStats = statCategories.map(statCategory => {
        let homeTeamValue = homeTeam.game_stats[statCategory.valueKey]
        let visitingTeamValue = visitingTeam.game_stats[statCategory.valueKey]

        if (typeof homeTeamValue === 'string' || homeTeamValue instanceof String) {
            homeTeamValue = parseFloat(homeTeamValue) || 0
        }
        if (typeof visitingTeamValue === 'string' || visitingTeamValue instanceof String) {
            visitingTeamValue = parseFloat(visitingTeamValue) || 0
        }

        if (statCategory.isPercent) {
            homeTeamValue = homeTeamValue * 100
            visitingTeamValue = visitingTeamValue * 100
        }

        if (
            (!homeTeamValue && homeTeamValue !== 0) ||
            (!visitingTeamValue && visitingTeamValue !== 0)
        ) {
            return null
        }

        return {
            title: statCategory.title,
            homeTeamValue: homeTeamValue,
            visitingTeamValue: visitingTeamValue,
            valueFormatter: statCategory.valueFormatter,
            isPercent: statCategory.isPercent,
        }
    })
    return gameStats.filter(n => n) // filter out null values
}

export function formatSeasonRecord(team) {
    if (team?.season_stats?.wins && team?.season_stats?.losses) {
        return `${team.season_stats.wins}-${team.season_stats.losses}`
    } else {
        return ''
    }
}

export function formatTimeoutsAndFouls(team, fouls) {
    return `TO: ${team?.full_timeouts_left} | TF: ${fouls}`
}
